// bluaIQ — AI-DRAFT-TAKEOFF-1: read-only demo shell for the AI Draft Takeoff.
//
// DEMO / READ-ONLY ONLY. Renders the static `aiDraftTakeoffDemoFixture` (typed
// against the INTEGRATION-MAP-1 contracts) as a preliminary review surface. It
// performs NO network I/O (no fetch/apiFetch), makes NO POST/PATCH/DELETE, writes
// nothing, and reaches no downstream module. Every downstream proposal card below is
// locked/read-only — human approval is required before anything could ever be
// written, and that wiring does not exist. Bilingual copy is inline (EN/ES).

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { aiDraftTakeoffDemoFixture, aiDraftTakeoffDemoQueueItems } from './aiDraftTakeoffDemoFixture'

// Inline bilingual copy — the exact strings the shell must show (also mirrored in
// src/locales/{en,es}.json under the `aiDraftTakeoff` namespace).
const COPY = {
  title: { en: 'AI Draft Takeoff', es: 'Cuantificación preliminar IA' },
  preliminary: { en: 'Preliminary demo only', es: 'Solo demo preliminar' },
  demoData: { en: 'Demo Data', es: 'Datos demo' },
  noRecords: { en: 'No downstream records are created', es: 'No crea registros en otros módulos' },
  humanApproval: {
    en: 'Human approval is required before any downstream write',
    es: 'Se requiere aprobación humana antes de crear datos en cualquier módulo',
  },
  stageGroups: { en: 'Stage groups', es: 'Grupos por etapa' },
  quantityRows: { en: 'Quantity rows', es: 'Filas de cantidad' },
  confidence: { en: 'Confidence', es: 'Confianza' },
  source: { en: 'Source', es: 'Fuente' },
  assumption: { en: 'Assumption', es: 'Supuesto' },
  blocker: { en: 'Blocker', es: 'Bloqueo' },
  forbidden: { en: 'Forbidden category', es: 'Categoría no permitida' },
  noSilentDrop: { en: 'No silent drop', es: 'Sin descartes silenciosos' },
  retained: { en: 'retained', es: 'conservados' },
  dropped: { en: 'dropped', es: 'descartados' },
  downstream: { en: 'Downstream proposals (locked / read-only)', es: 'Propuestas a otros módulos (bloqueadas / solo lectura)' },
  locked: { en: 'Locked — read only', es: 'Bloqueado — solo lectura' },
  // AI-DRAFT-TAKEOFF-2: review-queue copy.
  reviewQueue: { en: 'Review queue', es: 'Cola de revisión' },
  qPreliminary: { en: 'Preliminary AI draft', es: 'Borrador preliminar IA' },
  qRequiresReview: { en: 'Requires human review', es: 'Requiere revisión humana' },
  qBlocked: { en: 'Blocked', es: 'Bloqueado' },
  qSuperseded: { en: 'Superseded', es: 'Reemplazado' },
  readOnly: { en: 'Read-only', es: 'Solo lectura' },
  readOnlyDemo: { en: 'Read-only demo', es: 'Demo de solo lectura' },
  noApprovalAction: {
    en: 'No approval action is available in this demo',
    es: 'No hay acción de aprobación disponible en este demo',
  },
  queueDetail: { en: 'Selected item (read-only)', es: 'Elemento seleccionado (solo lectura)' },
  status: { en: 'Status', es: 'Estado' },
  // Downstream card titles.
  cardBudget: { en: 'Budget draft', es: 'Borrador de presupuesto' },
  cardRfq: { en: 'RFQ package', es: 'Paquete RFQ' },
  cardContract: { en: 'Contract draft input', es: 'Entrada de borrador de contrato' },
  cardSchedule: { en: 'Payment schedule draft input', es: 'Entrada de borrador de calendario de pagos' },
  cardGantt: { en: 'Gantt proposal', es: 'Propuesta de Gantt' },
  cardCalendar: { en: 'Calendar proposal', es: 'Propuesta de calendario' },
  cardDashboard: { en: 'Dashboard read-only', es: 'Panel solo lectura' },
} as const

const DOWNSTREAM_CARDS = [
  'cardBudget', 'cardRfq', 'cardContract', 'cardSchedule', 'cardGantt', 'cardCalendar', 'cardDashboard',
] as const

// AI-DRAFT-TAKEOFF-2: review-queue status → copy-key + badge-color lookup. Every
// status the demo fixture uses (AI_DRAFT, HUMAN_REVIEW_REQUIRED, BLOCKED, and the
// optional SUPERSEDED) maps to a label here; anything else falls back to the plain
// status string so an unmapped value never renders blank.
const QUEUE_STATUS_COPY_KEY = {
  AI_DRAFT: 'qPreliminary',
  HUMAN_REVIEW_REQUIRED: 'qRequiresReview',
  APPROVED_FOR_DRAFT: 'qPreliminary',
  APPROVED_FOR_WRITE: 'qPreliminary',
  BLOCKED: 'qBlocked',
  SUPERSEDED: 'qSuperseded',
} as const

const QUEUE_STATUS_BADGE_CLASS: Record<string, string> = {
  AI_DRAFT: 'bg-blue-50 text-blue-700',
  HUMAN_REVIEW_REQUIRED: 'bg-amber-50 text-amber-800',
  APPROVED_FOR_DRAFT: 'bg-blue-50 text-blue-700',
  APPROVED_FOR_WRITE: 'bg-blue-50 text-blue-700',
  BLOCKED: 'bg-red-50 text-red-700',
  SUPERSEDED: 'bg-gray-100 text-gray-600',
}

export default function AiDraftTakeoffShell() {
  const { i18n } = useTranslation()
  const isEs = i18n.language === 'es'
  const L = (k: { en: string; es: string }) => (isEs ? k.es : k.en)

  const item = aiDraftTakeoffDemoFixture
  const p = item.payload

  const queueItems = aiDraftTakeoffDemoQueueItems
  const [selectedQueueId, setSelectedQueueId] = useState<string>(queueItems[0]?.handoffId ?? '')
  const selectedQueueItem = queueItems.find((q) => q.handoffId === selectedQueueId) ?? queueItems[0]

  const queueStatusLabel = (status: string) => {
    const key = QUEUE_STATUS_COPY_KEY[status as keyof typeof QUEUE_STATUS_COPY_KEY]
    return key ? L(COPY[key]) : status
  }
  const queueStatusBadgeClass = (status: string) => QUEUE_STATUS_BADGE_CLASS[status] ?? 'bg-gray-100 text-gray-600'

  const DemoBadge = () => (
    <span
      data-testid="ai-draft-demo-badge"
      className="inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-[9px] font-[900] uppercase tracking-wide px-2 py-0.5 whitespace-nowrap"
    >
      {L(COPY.demoData)}
    </span>
  )

  return (
    <div data-testid="ai-draft-takeoff-shell" className="p-4 sm:p-5 max-w-5xl mx-auto min-w-0 space-y-4">
      {/* Top warning banner */}
      <div
        data-testid="ai-draft-warning-banner"
        className="rounded-xl border border-amber-300 bg-amber-50 px-3 py-3 flex flex-wrap items-start gap-2 min-w-0"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-[15px] font-[900] text-amber-900 break-words">{L(COPY.title)}</h1>
            <DemoBadge />
          </div>
          <p className="text-[11px] font-[800] text-amber-800 mt-0.5">{L(COPY.preliminary)}</p>
          <ul className="mt-1.5 space-y-0.5 text-[11px] font-[700] text-amber-900">
            <li data-testid="ai-draft-no-records">• {L(COPY.noRecords)}</li>
            <li data-testid="ai-draft-human-approval">• {L(COPY.humanApproval)}</li>
          </ul>
        </div>
      </div>

      {/* AI-DRAFT-TAKEOFF-2: read-only review queue (simulated, static fixture only). */}
      <section data-testid="ai-draft-review-queue" className="space-y-2">
        <h2 className="text-[12px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-2">
          {L(COPY.reviewQueue)} <DemoBadge />
          <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
            {L(COPY.readOnly)}
          </span>
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 min-w-0">
          {/* Queue column — stacks above the detail panel on mobile, sits beside it on desktop. */}
          <div className="lg:col-span-1 flex flex-col gap-2 min-w-0">
            {queueItems.map((q) => {
              const isSelected = q.handoffId === selectedQueueItem?.handoffId
              return (
                <button
                  key={q.handoffId}
                  type="button"
                  data-testid={`ai-draft-queue-item-${q.handoffId}`}
                  onClick={() => setSelectedQueueId(q.handoffId)}
                  aria-pressed={isSelected}
                  className={`text-left rounded-lg border p-2.5 min-w-0 transition-colors ${
                    isSelected ? 'border-[#00BCD4] bg-[#F0FCFE]' : 'border-[#E4EDF6] bg-white'
                  }`}
                >
                  <div className="flex flex-wrap items-center justify-between gap-1.5">
                    <span className="text-[11px] font-[900] text-[#1C1A17] break-words min-w-0 flex-1">
                      {q.payload.stageGroups[0]?.stageLabel ?? q.handoffId}
                    </span>
                    <span
                      data-testid="ai-draft-queue-status-badge"
                      className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap ${queueStatusBadgeClass(q.approvalState)}`}
                    >
                      {queueStatusLabel(q.approvalState)}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <DemoBadge />
                    <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
                      {L(COPY.readOnly)}
                    </span>
                  </div>
                  <p className="mt-1 text-[9px] font-[700] text-[#9E9890] break-words">{L(COPY.noRecords)}</p>
                </button>
              )
            })}
          </div>

          {/* Detail panel — read-only/demo only, no approve/save/create/export/sync action. */}
          {selectedQueueItem && (
            <div data-testid="ai-draft-queue-detail" className="lg:col-span-2 rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0 space-y-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0">{L(COPY.queueDetail)}</h3>
                <span
                  className={`inline-flex items-center rounded-full text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap ${queueStatusBadgeClass(selectedQueueItem.approvalState)}`}
                >
                  {L(COPY.status)}: {queueStatusLabel(selectedQueueItem.approvalState)}
                </span>
              </div>

              <p data-testid="ai-draft-queue-readonly-demo" className="text-[10px] font-[900] text-amber-800 break-words">
                {L(COPY.readOnlyDemo)}
              </p>
              <p data-testid="ai-draft-queue-no-approval-action" className="text-[10px] font-[700] text-[#6B655C] break-words">
                {L(COPY.noApprovalAction)}
              </p>

              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[9px] font-[900] uppercase tracking-wide text-[#9E9890]">{L(COPY.source)}:</span>
                <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
                  {selectedQueueItem.provenance.originKind}
                </span>
                <span
                  data-testid="ai-draft-queue-confidence-badge"
                  className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                >
                  {L(COPY.confidence)} {Math.round(selectedQueueItem.confidence.value * 100)}%
                </span>
              </div>

              {selectedQueueItem.assumptions.length > 0 && (
                <div className="flex flex-col gap-1">
                  {selectedQueueItem.assumptions.map((a) => (
                    <span key={a.id} className="inline-flex items-start rounded-md bg-purple-50 text-purple-700 text-[10px] font-[700] px-2 py-1 break-words min-w-0">
                      {L(COPY.assumption)}: {a.statement}
                    </span>
                  ))}
                </div>
              )}

              {selectedQueueItem.blockers.length > 0 && (
                <div className="flex flex-col gap-1">
                  {selectedQueueItem.blockers.map((b) => (
                    <span
                      key={b.code}
                      className={`inline-flex items-start rounded-md text-[10px] font-[800] px-2 py-1 break-words min-w-0 ${
                        b.isBlocking ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                      }`}
                    >
                      {b.severity}: {b.message}
                    </span>
                  ))}
                </div>
              )}

              {selectedQueueItem.forbiddenCategories.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {selectedQueueItem.forbiddenCategories.map((f) => (
                    <span key={f.code} className="inline-flex items-center rounded-full bg-[#F6D9DB] text-[#8A1A20] text-[9px] font-[800] px-2 py-0.5 max-w-full break-words">
                      {f.label}
                    </span>
                  ))}
                </div>
              )}

              <p className="text-[9px] font-[700] text-[#6B655C]">
                {selectedQueueItem.noSilentDrop.retained} {L(COPY.retained)} · {selectedQueueItem.noSilentDrop.dropped.length} {L(COPY.dropped)} / {selectedQueueItem.noSilentDrop.totalCandidates}
              </p>
            </div>
          )}
        </div>
      </section>

      {/* 1-2. Stage groups + quantity rows (with confidence + assumptions per row) */}
      <section data-testid="ai-draft-stage-groups" className="space-y-3">
        <h2 className="text-[12px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-2">
          {L(COPY.stageGroups)} <DemoBadge />
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {p.stageGroups.map((stage) => (
            <div key={stage.stageKey} data-testid={`ai-draft-stage-${stage.stageKey}`} className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[12px] font-[900] text-[#1C1A17] break-words min-w-0">{stage.stageLabel}</h3>
                <DemoBadge />
              </div>
              <div data-testid="ai-draft-quantity-rows" className="mt-2 space-y-2">
                {stage.quantityRows.map((row) => (
                  <div key={row.upstreamId} className="rounded-md bg-[#F7FAFD] border border-[#E4EDF6] px-2.5 py-2 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
                      <span className="text-[11px] font-[800] text-[#1C1A17] break-words min-w-0 flex-1">{row.description}</span>
                      <span className="text-[11px] font-[900] text-[#1C1A17] whitespace-nowrap">
                        {row.quantity} {row.unit}
                      </span>
                    </div>
                    {/* 3. Confidence badge */}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <span
                        data-testid="ai-draft-confidence-badge"
                        className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap"
                      >
                        {L(COPY.confidence)} {Math.round(row.confidence.value * 100)}%
                      </span>
                      {/* 5. Assumption badges (per row) */}
                      {(row.assumptions ?? []).map((a) => (
                        <span
                          key={a.id}
                          data-testid="ai-draft-assumption-badge"
                          className="inline-flex items-center rounded-full bg-purple-50 text-purple-700 text-[9px] font-[800] px-2 py-0.5 max-w-full break-words"
                        >
                          {L(COPY.assumption)}: {a.statement}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 4. Source / provenance badges */}
      <section data-testid="ai-draft-provenance" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-[10px] font-[900] uppercase tracking-wide text-[#9E9890]">{L(COPY.source)}:</span>
          <span data-testid="ai-draft-origin-badge" className="inline-flex items-center rounded-full bg-gray-100 text-gray-700 text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
            {item.provenance.originKind}
          </span>
          {(item.provenance.sourceDocumentRefs ?? []).map((ref) => (
            <span key={ref} className="inline-flex items-center rounded-full bg-gray-50 text-gray-600 text-[9px] font-[700] px-2 py-0.5 max-w-full break-words">
              {ref}
            </span>
          ))}
        </div>
      </section>

      {/* 5. Envelope-level assumption badges + 6. Blocker badges */}
      <section className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div data-testid="ai-draft-assumptions" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
          <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.assumption)}</h2>
          <div className="flex flex-col gap-1.5">
            {item.assumptions.map((a) => (
              <span key={a.id} className="inline-flex items-start rounded-md bg-purple-50 text-purple-700 text-[10px] font-[700] px-2 py-1 break-words min-w-0">
                {a.statement}
              </span>
            ))}
          </div>
        </div>
        <div data-testid="ai-draft-blockers" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
          <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.blocker)}</h2>
          <div className="flex flex-col gap-1.5">
            {item.blockers.map((b) => (
              <span
                key={b.code}
                data-testid="ai-draft-blocker-badge"
                className={`inline-flex items-start rounded-md text-[10px] font-[800] px-2 py-1 break-words min-w-0 ${
                  b.isBlocking ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-800'
                }`}
              >
                {b.severity}: {b.message}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Forbidden-category badges */}
      {item.forbiddenCategories.length > 0 && (
        <section data-testid="ai-draft-forbidden" className="rounded-lg border border-[#E7C3C5] bg-[#FDF2F2] p-3 min-w-0">
          <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#B4232A] mb-1.5">{L(COPY.forbidden)}</h2>
          <div className="flex flex-wrap gap-1.5">
            {item.forbiddenCategories.map((f) => (
              <span key={f.code} data-testid="ai-draft-forbidden-badge" className="inline-flex items-center rounded-full bg-[#F6D9DB] text-[#8A1A20] text-[9px] font-[800] px-2 py-0.5 max-w-full break-words">
                {f.label}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* 8. No-silent-drop summary */}
      <section data-testid="ai-draft-no-silent-drop" className="rounded-lg border border-[#E4EDF6] bg-white p-3 min-w-0">
        <h2 className="text-[11px] font-[900] uppercase tracking-wide text-[#9E9890] mb-1.5">{L(COPY.noSilentDrop)}</h2>
        <p className="text-[10px] font-[800] text-[#6B655C]">
          {item.noSilentDrop.retained} {L(COPY.retained)} · {item.noSilentDrop.dropped.length} {L(COPY.dropped)} / {item.noSilentDrop.totalCandidates}
        </p>
        <div className="mt-1.5 space-y-1">
          {item.noSilentDrop.dropped.map((d) => (
            <div key={d.upstreamId} className="text-[10px] font-[700] text-[#6B655C] break-words min-w-0">
              • <span className="font-[900]">{d.upstreamId}</span> — {d.reason}
            </div>
          ))}
        </div>
      </section>

      {/* 9. Locked / read-only downstream proposal cards */}
      <section data-testid="ai-draft-downstream" className="space-y-2">
        <h2 className="text-[12px] font-[900] uppercase tracking-wide text-[#9E9890] flex flex-wrap items-center gap-2">
          {L(COPY.downstream)} <DemoBadge />
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {DOWNSTREAM_CARDS.map((key) => (
            <div
              key={key}
              data-testid={`ai-draft-downstream-card-${key}`}
              aria-disabled="true"
              className="rounded-lg border border-[#D6CFC4] bg-[#F3F1EC] p-3 opacity-70 cursor-not-allowed min-w-0"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[11px] font-[900] text-[#6B655C] break-words min-w-0">{L(COPY[key])}</h3>
                <span data-testid="ai-draft-locked-badge" className="inline-flex items-center rounded-full bg-[#E4DFD6] text-[#6B655C] text-[9px] font-[800] px-2 py-0.5 whitespace-nowrap">
                  🔒 {L(COPY.locked)}
                </span>
              </div>
              <p className="mt-1 text-[10px] font-[700] text-[#9E9890]">{L(COPY.noRecords)}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
